import { adminApi } from "./adminApi";
import { userAPI } from "./api";
import { getAuth } from "../utils/auth";

// Standard tier rank priority order (Free -> Basic -> Standard -> Premium -> Pro/YTT -> custom tiers at bottom)
const RANK_ORDER = ["free", "trial", "basic", "standard", "premium", "pro", "ytt"];

let cachedPackages = [];
let cachedTiers = ["Free Trial", "Basic", "Standard", "Premium", "Pro"];
let isFetching = false;

export const packageService = {
  /**
   * Sort tiers logically by their access level rank (0..4) and then alphabetically
   */
  normalizeTierName(name) {
    if (!name) return "";
    const low = name.toLowerCase().trim();
    if (low === "free" || low.includes("trial")) return "Free Trial";
    if (low === "basic" || low === "basic plan") return "Basic";
    if (low === "standard" || low === "standard plan") return "Standard";
    if (low === "premium" || low === "premium plan" || low === "premium annual plan") return "Premium";
    if (low === "ytt" || low === "ytt pro" || low === "pro") return "YTT Pro";
    if (low === "vip") return "VIP";
    return name.trim().split(" ").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
  },

  sortTiers(tierList, packageList = []) {
    const list = Array.from(new Set(tierList.filter(Boolean).map(t => this.normalizeTierName(t))));
    const getRank = (name) => {
      const low = name.toLowerCase().trim();
      if (low === "free" || low === "free trial" || low.includes("trial")) return 0;
      const matched = packageList.find(p => 
        (p.name && this.normalizeTierName(p.name).toLowerCase() === low) ||
        (p.packageType && this.normalizeTierName(p.packageType).toLowerCase() === low)
      );
      if (matched && typeof matched.accessLevel === "number") return matched.accessLevel;
      if (low.includes("pro") || low.includes("ytt") || low.includes("vip")) return 4;
      if (low.includes("premium") || low.includes("gold")) return 3;
      if (low.includes("standard") || low.includes("silver")) return 2;
      if (low.includes("basic") || low.includes("bronze")) return 1;
      return 1;
    };

    return list.sort((a, b) => {
      const rankA = getRank(a);
      const rankB = getRank(b);
      if (rankA !== rankB) return rankA - rankB;
      return a.localeCompare(b);
    });
  },

  /**
   * Fetch dynamic packages from DB and broadcast real-time tier updates
   */
  async fetchPackages() {
    if (isFetching) return { packages: cachedPackages, tiers: cachedTiers };
    isFetching = true;
    try {
      let pkgList = [];
      const auth = getAuth();
      const isAdmin = (auth?.role || "").toLowerCase().includes("admin");

      // 1. Try public packages endpoint first (accessible to everyone including guests)
      const pubRes = await userAPI.getPublicPackages().catch(() => null);
      if (pubRes?.data?.data && Array.isArray(pubRes.data.data)) {
        pkgList = pubRes.data.data;
      } else if (pubRes?.data && Array.isArray(pubRes.data)) {
        pkgList = pubRes.data;
      }

      // 2. If empty and admin, fetch full admin packages
      if (pkgList.length === 0 && isAdmin) {
        const res = await adminApi.getPackages().catch(() => null);
        if (res?.data && Array.isArray(res.data)) {
          pkgList = res.data;
        }
      }

      // 3. Fallback to logged-in user subscription packages
      if (pkgList.length === 0) {
        const userRes = await userAPI.getUserSubscription().catch(() => null);
        pkgList = userRes?.data?.data?.packages || userRes?.data?.data?.Packages || [];
      }
      // Only include active non-deleted packages in dynamic tier dropdowns across all pages
      const activePackages = pkgList.filter(pkg => pkg.isActive !== false);
      cachedPackages = activePackages;

      const seenCanonical = new Set(["free trial"]);
      const distinctTiers = ["Free Trial"];

      activePackages.forEach(pkg => {
        const rawName = pkg.packageType || pkg.name || "";
        const canonical = this.normalizeTierName(rawName);
        if (canonical && !seenCanonical.has(canonical.toLowerCase())) {
          seenCanonical.add(canonical.toLowerCase());
          distinctTiers.push(canonical);
        }
      });

      if (distinctTiers.length > 0) {
        cachedTiers = this.sortTiers(distinctTiers, activePackages);
      } else {
        cachedTiers = ["Free Trial", "Basic", "Standard", "Premium", "YTT Pro"];
      }

      window.dispatchEvent(new CustomEvent("packages-updated", { 
        detail: { packages: cachedPackages, tiers: cachedTiers } 
      }));

      return { packages: cachedPackages, tiers: cachedTiers };
    } catch (e) {
      console.warn("Failed to fetch dynamic packages:", e);
      return { packages: cachedPackages, tiers: cachedTiers };
    } finally {
      isFetching = false;
    }
  },

  getCachedPackages() {
    return cachedPackages;
  },

  getCachedTiers() {
    return cachedTiers;
  },

  notifyPackageChange() {
    this.fetchPackages();
    window.dispatchEvent(new Event("subscription-updated"));
  }
};
